/**
 * AlfeSync watcher — recursive file watcher with debounce and ignore support.
 *
 * Uses chokidar to watch the workspace root, debounces per-file changes
 * by 2 seconds, and emits batches of changed paths.
 */

import { watch } from "chokidar";
import { relative } from "node:path";
import { loadIgnorePatterns, shouldIgnore, shouldIgnoreDir } from "./ignore.js";

export interface WatcherOptions {
  workspacePath: string;
  /**
   * Active agent runtime — selects the per-runtime default ignore set so the
   * watcher never descends into runtime-internal dirs (openclaw `npm/`,
   * hermes `.venv/`, …). Default: "openclaw".
   */
  runtime?: string;
  /** Debounce delay per file in milliseconds. Default: 2000 */
  debounceMs?: number;
  /** Callback invoked with batches of changed relative paths */
  onChanges: (paths: string[]) => void | Promise<void>;
}

/**
 * Start watching a workspace for file changes.
 *
 * Returns a cleanup function to stop watching.
 */
export async function startWatcher(
  options: WatcherOptions,
): Promise<() => Promise<void>> {
  const { workspacePath, runtime = "openclaw", debounceMs = 2000, onChanges } = options;
  const ignorePatterns = await loadIgnorePatterns(workspacePath, runtime);

  // Pending changes map: relativePath → timeout handle
  const pending = new Map<string, ReturnType<typeof setTimeout>>();
  // Batch accumulator for flushing
  let batchPaths = new Set<string>();
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleBatch() {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      if (batchPaths.size === 0) return;

      const paths = [...batchPaths];
      batchPaths = new Set();
      void onChanges(paths);
    }, debounceMs);
  }

  function handleChange(absolutePath: string) {
    // Posix-normalise so the globs (which are posix-style) match on Windows
    // self-hosted agents too — parity with the `ignored` predicate below.
    const relativePath = relative(workspacePath, absolutePath).replace(/\\/g, "/");

    // Skip ignored files
    if (shouldIgnore(relativePath, ignorePatterns)) return;

    // Clear existing timer for this file
    const existingTimer = pending.get(relativePath);
    if (existingTimer) clearTimeout(existingTimer);

    // Debounce: wait before adding to batch
    const timer = setTimeout(() => {
      pending.delete(relativePath);
      batchPaths.add(relativePath);
      scheduleBatch();
    }, debounceMs);

    pending.set(relativePath, timer);
  }

  const watcher = watch(workspacePath, {
    persistent: true,
    ignoreInitial: true,
    followSymlinks: false,
    depth: undefined, // unlimited depth
    // chokidar 4 dropped glob support in `ignored` — a string-glob array is
    // treated as literal paths and matches nothing, so the watcher would
    // create an inotify watch on every dir (incl. node_modules / the runtime's
    // npm cache) and exhaust the OS watch limit → ENOSPC crash-loop. Use a
    // predicate driven by the SAME ignore engine as event filtering; returning
    // true for a directory stops chokidar descending into it.
    // chokidar invokes this for files AND directories, twice per entry: first
    // with no stats (path check), then with stats before it actually watches.
    // Match ignored files directly; prune ignored directories (whose contents
    // are ignored) via the sentinel-child probe. When stats say it's a FILE,
    // skip the dir probe so a file literally named like an ignored dir (e.g. an
    // extensionless `dist`) isn't wrongly ignored — keeps parity with the walk,
    // which only dir-prunes real directories.
    ignored: (p: string, stats?: { isDirectory(): boolean }) => {
      const rel = relative(workspacePath, p).replace(/\\/g, "/");
      if (rel === "") return false; // never ignore the workspace root
      if (shouldIgnore(rel, ignorePatterns)) return true;
      if (stats && !stats.isDirectory()) return false;
      return shouldIgnoreDir(rel, ignorePatterns);
    },
  });

  watcher.on("add", handleChange);
  watcher.on("change", handleChange);
  watcher.on("unlink", handleChange);

  // Return cleanup function
  return async () => {
    // Clear all pending timers
    for (const timer of pending.values()) {
      clearTimeout(timer);
    }
    pending.clear();

    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    await watcher.close();
  };
}
